import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import { useI18n } from "../../i18n/I18nProvider";
import { getSolutions, getIndustries } from "../../i18n/content";

export default function Header() {
  const { t, locale } = useI18n();
  const VISIBLE_SOLUTIONS = getSolutions(locale).filter((s) => !s.hidden);
  const INDUSTRIES_LOC = getIndustries(locale);

  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const [openMenu, setOpenMenu] = React.useState(null);

  const productRef = React.useRef(null);
  const industriesRef = React.useRef(null);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    const onClickOutside = (e) => {
      if (
        !productRef.current?.contains(e.target) &&
        !industriesRef.current?.contains(e.target)
      ) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/75 border-b border-zinc-200/70" : "bg-transparent"
      }`}
    >
      <div className="container-oj flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
          <span className="font-display text-2xl md:text-[28px] font-black tracking-tighter text-oj-ink">
            o&#39;javana
          </span>
          <span className="h-2 w-2 rounded-full bg-oj-brand group-hover:bg-oj-pos transition-colors duration-300" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="primary-nav">
          <NavLink
            to="/"
            end
            data-testid="nav-home"
            className={({ isActive }) =>
              `px-3 py-2 text-sm font-medium tracking-tight rounded-md transition-colors ${
                isActive ? "text-oj-ink" : "text-zinc-600 hover:text-oj-ink"
              }`
            }
          >
            {t("header.home")}
          </NavLink>
          <div
            ref={productRef}
            className="relative"
            onMouseEnter={() => setOpenMenu("product")}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <button
              data-testid="nav-product"
              className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium tracking-tight rounded-md text-zinc-600 hover:text-oj-ink"
              onClick={() => setOpenMenu((m) => (m === "product" ? null : "product"))}
              aria-expanded={openMenu === "product"}
            >
              {t("header.product")} <ChevronDown className={`size-3.5 transition-transform ${openMenu === "product" ? "rotate-180" : ""}`} />
            </button>
            {openMenu === "product" && (
              <div data-testid="product-menu" className="absolute left-0 top-full pt-2 w-[640px]">
                <div className="rounded-2xl bg-white border border-zinc-200 shadow-xl p-4 grid grid-cols-2 gap-1">
                  {VISIBLE_SOLUTIONS.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/solutions/${s.slug}`}
                      data-testid={`product-menu-${s.slug}`}
                      onClick={() => setOpenMenu(null)}
                      className="group flex items-start gap-3 rounded-md px-3 py-2.5 hover:bg-zinc-50"
                    >
                      <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ background: s.color }} />
                      <span className="min-w-0">
                        <span className="block font-semibold text-oj-ink leading-tight">{s.name}</span>
                        <span className="block text-xs text-zinc-500 mt-0.5 truncate">{s.tagline}</span>
                      </span>
                    </Link>
                  ))}
                  <Link
                    to="/solutions"
                    onClick={() => setOpenMenu(null)}
                    className="col-span-2 mt-2 inline-flex items-center justify-between rounded-md bg-zinc-50 px-3 py-2.5 text-sm font-semibold text-oj-ink hover:bg-zinc-100"
                    data-testid="product-menu-all"
                  >
                    {t("header.see_all_products")} <ArrowUpRight className="size-4" />
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div
            ref={industriesRef}
            className="relative"
            onMouseEnter={() => setOpenMenu("industries")}
            onMouseLeave={() => setOpenMenu(null)}
          >
            <button
              data-testid="nav-industries"
              className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium tracking-tight rounded-md text-zinc-600 hover:text-oj-ink"
              onClick={() => setOpenMenu((m) => (m === "industries" ? null : "industries"))}
              aria-expanded={openMenu === "industries"}
            >
              {t("header.industries")} <ChevronDown className={`size-3.5 transition-transform ${openMenu === "industries" ? "rotate-180" : ""}`} />
            </button>
            {openMenu === "industries" && (
              <div data-testid="industries-menu" className="absolute left-0 top-full pt-2 w-[560px]">
                <div className="rounded-2xl bg-white border border-zinc-200 shadow-xl p-4 grid grid-cols-2 gap-1">
                  {INDUSTRIES_LOC.map((i) => (
                    <Link
                      key={i.slug}
                      to={`/industries#${i.slug}`}
                      data-testid={`industries-menu-${i.slug}`}
                      onClick={() => setOpenMenu(null)}
                      className="group flex items-start gap-3 rounded-md px-3 py-2.5 hover:bg-zinc-50"
                    >
                      <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ background: i.color }} />
                      <span className="min-w-0">
                        <span className="block font-semibold text-oj-ink leading-tight">{i.name}</span>
                        <span className="block text-xs text-zinc-500 mt-0.5 truncate">{i.blurb}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <NavLink
            to="/offers"
            data-testid="nav-offers"
            className={({ isActive }) =>
              `px-3 py-2 text-sm font-medium tracking-tight rounded-md transition-colors ${
                isActive ? "text-oj-brand" : "text-zinc-600 hover:text-oj-ink"
              }`
            }
          >
            {t("header.offers")}
          </NavLink>
          <NavLink
            to="/pricing"
            data-testid="nav-pricing"
            className={({ isActive }) =>
              `px-3 py-2 text-sm font-medium tracking-tight rounded-md transition-colors ${
                isActive ? "text-oj-ink" : "text-zinc-600 hover:text-oj-ink"
              }`
            }
          >
            {t("nav.pricing")}
          </NavLink>
          <NavLink
            to="/contact"
            data-testid="nav-contact"
            className={({ isActive }) =>
              `px-3 py-2 text-sm font-medium tracking-tight rounded-md transition-colors ${
                isActive ? "text-oj-ink" : "text-zinc-600 hover:text-oj-ink"
              }`
            }
          >
            {t("nav.contact")}
          </NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <LanguageSwitcher />
          <Link
            to="/#demo"
            data-testid="header-demo-cta"
            className="inline-flex items-center gap-1.5 bg-oj-brand text-oj-deep text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-[#6FBDB9] transition-colors"
          >
            {t("nav.demo")} <ArrowUpRight className="size-4" />
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <button
            data-testid="mobile-menu-toggle"
            className="p-2 rounded-md hover:bg-zinc-100"
            onClick={() => setOpen((s) => !s)}
            aria-label="Toggle menu"
          >
            {open ? <X className="size-6" /> : <Menu className="size-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-zinc-200 bg-white" data-testid="mobile-nav">
          <div className="container-oj py-4 flex flex-col gap-1">
            <NavLink
              to="/"
              end
              onClick={() => setOpen(false)}
              className="px-3 py-3 text-base font-medium text-zinc-800 hover:text-oj-ink rounded-md"
            >
              {t("header.home")}
            </NavLink>
            <details className="group">
              <summary className="px-3 py-3 text-base font-medium text-zinc-800 cursor-pointer flex items-center justify-between">
                {t("header.product")} <ChevronDown className="size-4 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="ml-3 pl-3 border-l border-zinc-200 mt-1 mb-2 space-y-1">
                {VISIBLE_SOLUTIONS.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/solutions/${s.slug}`}
                    onClick={() => setOpen(false)}
                    className="block px-3 py-2 text-sm text-zinc-700 hover:text-oj-ink"
                  >
                    {s.name}
                  </Link>
                ))}
              </div>
            </details>
            <details className="group">
              <summary className="px-3 py-3 text-base font-medium text-zinc-800 cursor-pointer flex items-center justify-between">
                {t("header.industries")} <ChevronDown className="size-4 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="ml-3 pl-3 border-l border-zinc-200 mt-1 mb-2 space-y-1">
                {INDUSTRIES_LOC.map((i) => (
                  <Link
                    key={i.slug}
                    to={`/industries#${i.slug}`}
                    onClick={() => setOpen(false)}
                    className="block px-3 py-2 text-sm text-zinc-700 hover:text-oj-ink"
                  >
                    {i.name}
                  </Link>
                ))}
              </div>
            </details>
            <NavLink
              to="/offers"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-offers"
              className="px-3 py-3 text-base font-medium text-zinc-800 hover:text-oj-ink rounded-md"
            >
              {t("header.offers")}
            </NavLink>
            <NavLink
              to="/pricing"
              onClick={() => setOpen(false)}
              className="px-3 py-3 text-base font-medium text-zinc-800 hover:text-oj-ink rounded-md"
            >
              {t("nav.pricing")}
            </NavLink>
            <NavLink
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-contact"
              className="px-3 py-3 text-base font-medium text-zinc-800 hover:text-oj-ink rounded-md"
            >
              {t("nav.contact")}
            </NavLink>
            <Link
              to="/#demo"
              onClick={() => setOpen(false)}
              data-testid="mobile-demo-cta"
              className="mt-2 inline-flex items-center justify-center gap-1.5 bg-oj-brand text-oj-deep text-sm font-semibold px-4 py-3 rounded-md"
            >
              {t("nav.demo")} <ArrowUpRight className="size-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
